/**
 * Pricing API Client Utility
 * Base HTTP client for making requests to the Pricing Service API
 */

import { ApiResponse } from './types';
import { PRICING_API_BASE_URL, getStoredToken } from '../config';

export interface PricingRequestConfig extends RequestInit {
  params?: Record<string, any>;
}

export class PricingApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL: string = PRICING_API_BASE_URL, headers: Record<string, string> = {}) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...headers,
    };
  }

  /**
   * Build URL with query parameters
   */
  private buildURL(endpoint: string, params?: Record<string, any>): string {
    // Check if baseURL is absolute (starts with http:// or https://)
    const isAbsoluteBase = /^https?:\/\//i.test(this.baseURL);

    let url: URL;
    if (isAbsoluteBase) {
      // Use URL constructor for absolute base URLs
      url = new URL(endpoint, this.baseURL);
    } else {
      // For relative base URLs, construct the path manually
      const basePath = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      const endpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const fullPath = `${basePath}${endpointPath}`;
      // Use window.location.origin as base for relative URLs
      url = new URL(fullPath, window.location.origin);
    }

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => url.searchParams.append(key, String(v)));
          } else {
            url.searchParams.append(key, String(value));
          }
        }
      });
    }

    // For absolute base URLs, return the full URL
    if (isAbsoluteBase) {
      return url.toString();
    }

    // For relative base URLs, return just the pathname + search to work with proxy
    return url.pathname + url.search;
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    endpoint: string,
    config: PricingRequestConfig = {}
  ): Promise<T> {
    const { params, headers, ...restConfig } = config;
    const url = this.buildURL(endpoint, params);

    try {
      const requestHeaders: Record<string, string> = { ...this.defaultHeaders, ...(headers as Record<string, string> || {}) };

      // // Inject auth token dynamically
      // const token = getStoredToken();
      // if (token) {
      //   requestHeaders['Authorization'] = `Bearer ${token}`;
      // }

      const response = await fetch(url, {
        ...restConfig,
        headers: requestHeaders,
      });

      if (!response.ok) {
        // if (response.status === 403) {
        //   window.dispatchEvent(new CustomEvent('auth:forbidden'));
        // }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP Error: ${response.status} ${response.statusText}`
        );
      }

      const data: T = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, params?: Record<string, any>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      params,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Update base URL
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
  }

  /**
   * Update default headers
   */
  setHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = {
      ...this.defaultHeaders,
      ...headers,
    };
  }
}

// Export singleton instance
export const pricingApiClient = new PricingApiClient();